"use client";

import { Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { createRandomId } from "@/lib/random-id";
import { MatchResult, ScoreCounter, ScoreInput } from "../base-match-template";

type SeriesDetailDraft = { id: string; team1Score: string; team2Score: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const parseOptionalInt = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  if (value.trim() === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampInt = (value: number, options: { min: number; max: number }) =>
  Math.max(options.min, Math.min(options.max, value));

export type SeriesScoreEditorProps = {
  matchScoreLabel: string;
  detailsLabel: string;
  detailsHint?: string;
  unitSingularLabel: string;
  matchScoreKeys: { your: string; opponent: string };
  detailsKey: "sets" | "games" | "frames";
  initialData?: Record<string, unknown> | null;
  maxDetailCount?: number;
  totalMax?: number;
  detailMaxScore?: number;
  team1Label?: string;
  team2Label?: string;
  onChange: (data: Record<string, unknown>) => void;
};

export function SeriesScoreEditor({
  matchScoreLabel,
  detailsLabel,
  detailsHint,
  unitSingularLabel,
  matchScoreKeys,
  detailsKey,
  initialData,
  maxDetailCount = 9,
  totalMax = 99,
  detailMaxScore,
  team1Label = "Team 1",
  team2Label = "Team 2",
  onChange,
}: SeriesScoreEditorProps) {
  const initialMatchScore = isRecord(initialData?.matchScore)
    ? (initialData?.matchScore as Record<string, unknown>)
    : null;

  const initialTeam1 = clampInt(
    parseOptionalInt(initialMatchScore?.[matchScoreKeys.your]) ?? 0,
    { min: 0, max: totalMax },
  );
  const initialTeam2 = clampInt(
    parseOptionalInt(initialMatchScore?.[matchScoreKeys.opponent]) ?? 0,
    { min: 0, max: totalMax },
  );

  const rawDetails = initialData?.[detailsKey];
  const initialDetails = Array.isArray(rawDetails)
    ? (rawDetails as unknown[])
        .map((row) => {
          if (!isRecord(row)) return null;
          const your = parseOptionalInt(
            row.yourScore ?? row.team1Score ?? row.team1,
          );
          const opponent = parseOptionalInt(
            row.opponentScore ?? row.team2Score ?? row.team2,
          );
          if (your == null || opponent == null) return null;
          return {
            id: createRandomId(),
            team1Score: String(your),
            team2Score: String(opponent),
          };
        })
        .filter((row): row is SeriesDetailDraft => row !== null)
    : [];

  const [showDetails, setShowDetails] = useState(initialDetails.length > 0);
  const [team1Total, setTeam1Total] = useState(initialTeam1);
  const [team2Total, setTeam2Total] = useState(initialTeam2);
  const [detailDrafts, setDetailDrafts] =
    useState<SeriesDetailDraft[]>(initialDetails);

  const validDetails = useMemo(() => {
    const maxScore = detailMaxScore ?? 999;
    return detailDrafts
      .map((row) => {
        if (row.team1Score === "" || row.team2Score === "") return null;
        const team1 = Number.parseInt(row.team1Score, 10);
        const team2 = Number.parseInt(row.team2Score, 10);
        if (
          Number.isNaN(team1) ||
          Number.isNaN(team2) ||
          team1 < 0 ||
          team2 < 0 ||
          team1 > maxScore ||
          team2 > maxScore
        ) {
          return null;
        }
        return { yourScore: team1, opponentScore: team2 };
      })
      .filter(
        (row): row is { yourScore: number; opponentScore: number } =>
          row !== null,
      );
  }, [detailDrafts, detailMaxScore]);

  const derivedWon = useMemo(() => {
    let team1 = 0;
    let team2 = 0;
    for (const row of validDetails) {
      if (row.yourScore > row.opponentScore) team1++;
      if (row.opponentScore > row.yourScore) team2++;
    }
    return { team1, team2 };
  }, [validDetails]);

  useEffect(() => {
    if (!showDetails) return;
    setTeam1Total(clampInt(derivedWon.team1, { min: 0, max: totalMax }));
    setTeam2Total(clampInt(derivedWon.team2, { min: 0, max: totalMax }));
  }, [derivedWon.team1, derivedWon.team2, showDetails, totalMax]);

  useEffect(() => {
    if (!showDetails) return;
    if (detailDrafts.length > 0) return;
    setDetailDrafts([{ id: createRandomId(), team1Score: "", team2Score: "" }]);
  }, [showDetails, detailDrafts.length]);

  useEffect(() => {
    const next: Record<string, unknown> = {
      matchScore: {
        [matchScoreKeys.your]: team1Total,
        [matchScoreKeys.opponent]: team2Total,
      },
      [detailsKey]: showDetails ? validDetails : [],
    };
    onChange(next);
  }, [
    detailsKey,
    matchScoreKeys.opponent,
    matchScoreKeys.your,
    onChange,
    showDetails,
    team1Total,
    team2Total,
    validDetails,
  ]);

  const handleDetailScoreChange = (
    index: number,
    field: "team1Score" | "team2Score",
    value: string,
  ) => {
    setDetailDrafts((prev) => {
      const next = [...prev];
      const row = next[index];
      if (!row) return prev;
      next[index] = { ...row, [field]: value };
      return next;
    });
  };

  const handleAddDetail = () => {
    if (detailDrafts.length >= maxDetailCount) return;
    setDetailDrafts((prev) => [
      ...prev,
      { id: createRandomId(), team1Score: "", team2Score: "" },
    ]);
  };

  const handleRemoveDetail = () => {
    setDetailDrafts((prev) => prev.slice(0, -1));
  };

  const handleClearDetails = () => {
    setShowDetails(false);
    setDetailDrafts([]);
  };

  const setTeam1TotalSafe = (next: number) => {
    setTeam1Total(clampInt(next, { min: 0, max: totalMax }));
  };

  const setTeam2TotalSafe = (next: number) => {
    setTeam2Total(clampInt(next, { min: 0, max: totalMax }));
  };

  return (
    <div className="space-y-3">
      <Label className="text-text-primary">{matchScoreLabel}</Label>
      <div className="flex items-center justify-center gap-4">
        <ScoreCounter
          label={team1Label}
          value={team1Total}
          onChange={setTeam1TotalSafe}
          isWinner={team1Total > team2Total}
          variant="brand"
          disabled={showDetails}
        />
        <span className="text-2xl font-bold text-text-secondary">–</span>
        <ScoreCounter
          label={team2Label}
          value={team2Total}
          onChange={setTeam2TotalSafe}
          isWinner={team2Total > team1Total}
          variant="opponent"
          disabled={showDetails}
        />
      </div>
      <MatchResult yourScore={team1Total} opponentScore={team2Total} />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-text-primary">{detailsLabel}</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? "Totals only" : "Add details"}
          </Button>
        </div>

        {showDetails && (
          <div className="space-y-4 rounded-3xl bg-surface-alt/40 p-5">
            {detailsHint ? (
              <p className="text-xs text-text-secondary">{detailsHint}</p>
            ) : null}

            <div className="space-y-2">
              {detailDrafts.map((row, index) => (
                <div key={row.id} className="flex items-center justify-between">
                  <span className="text-xs font-semibold tracking-wider text-text-secondary uppercase">
                    {unitSingularLabel} {index + 1}
                  </span>
                  <div className="flex items-center gap-3">
                    <ScoreInput
                      value={row.team1Score}
                      onChange={(e) =>
                        handleDetailScoreChange(
                          index,
                          "team1Score",
                          e.target.value,
                        )
                      }
                      placeholder="-"
                      min={0}
                      max={detailMaxScore}
                    />
                    <span className="font-medium text-text-tertiary">–</span>
                    <ScoreInput
                      value={row.team2Score}
                      onChange={(e) =>
                        handleDetailScoreChange(
                          index,
                          "team2Score",
                          e.target.value,
                        )
                      }
                      placeholder="-"
                      min={0}
                      max={detailMaxScore}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddDetail}
                disabled={detailDrafts.length >= maxDetailCount}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add {unitSingularLabel.toLowerCase()}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemoveDetail}
                disabled={detailDrafts.length <= 1}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                Remove
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearDetails}
                className="text-error hover:bg-error/10 hover:text-error"
              >
                <X className="mr-1 h-4 w-4" />
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
